import { format } from 'date-fns';
import { User, Calendar } from 'lucide-react';
import type { AuditedRecord } from '@prolarus/shared';

interface RecordMetadataProps {
  record: AuditedRecord | null | undefined;
}

export default function RecordMetadata({ record }: RecordMetadataProps) {
  const created = record?.created ?? null;
  const modified = record?.modified ?? null;
  const createdByName = record?.created_by_profile?.full_name ?? null;
  const modifiedByName = record?.updated_by_profile?.full_name ?? null;

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch { return '—'; }
  };

  const formatTime = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch { return null; }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Created Info */}
      <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
          <Calendar className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</p>
          <p className="text-sm text-foreground">{formatDateOnly(created)}</p>
          {formatTime(created) && (
            <p className="text-xs text-muted-foreground">at {formatTime(created)}</p>
          )}
          {createdByName && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3" />
              {createdByName}
            </p>
          )}
        </div>
      </div>

      {/* Modified Info */}
      <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
          <Calendar className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Modified</p>
          <p className="text-sm text-foreground">{formatDateOnly(modified)}</p>
          {formatTime(modified) && (
            <p className="text-xs text-muted-foreground">at {formatTime(modified)}</p>
          )}
          {modifiedByName && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3" />
              {modifiedByName}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
